import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, Crown, BadgeCheck, Sparkles, Loader2, ArrowRight } from "lucide-react";

// Friendly trophy color per top-3 position
const POSITION_COLORS = ["#D9923B", "#A8A8A8", "#9E6F3F"]; // gold, silver, bronze

const monthLabel = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const dt = new Date(Number(y), Number(m) - 1, 1);
  return dt.toLocaleDateString(undefined, { month: "long", year: "numeric" });
};

const ReferralLeaderboard = () => {
  const [data, setData] = useState(null);
  const [meRow, setMeRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/referral-leaderboard"),
      user ? api.get("/me/referral-leaderboard-position").catch(() => ({ data: null })) : Promise.resolve({ data: null }),
    ])
      .then(([lb, mine]) => {
        setData(lb.data);
        setMeRow(mine?.data || null);
      })
      .finally(() => setLoading(false));
  }, [user]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin"/></div>;
  }
  if (!data) return null;

  return (
    <div className="container-narrow py-10" data-testid="referral-leaderboard-page">
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1A2024] text-white text-[10px] uppercase tracking-[0.25em] mb-4">
          <Trophy className="w-3 h-3"/> Referral leaderboard · {monthLabel(data.month)}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight mb-3">
          Top {data.prize_top_n} sharers this month win <span className="text-[#C05A3C]">£{data.prize_gbp}</span> credit each.
        </h1>
        <p className="text-muted-foreground max-w-2xl">
          Every sale you refer earns you a piece of the platform fee — and the {data.prize_top_n} most generous sharers each month win a bonus they can spend anywhere on AfriMarket.
        </p>
      </div>

      {/* Your row (if logged in) */}
      {meRow && (
        <Card className="p-5 mb-8 bg-gradient-to-br from-[#FFF7EE] to-white border-[#D9923B]/30" data-testid="my-leaderboard-row">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#1A2024] text-white flex items-center justify-center font-display text-lg">
                {meRow.me.position ? `#${meRow.me.position}` : "—"}
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Your standing this month</div>
                <div className="font-display text-2xl">£{(meRow.me.total || 0).toFixed(2)} <span className="text-sm text-muted-foreground font-sans">from {meRow.me.count || 0} sales</span></div>
                {meRow.gap_to_prize_zone !== null && meRow.gap_to_prize_zone > 0 && !meRow.me.in_prize_zone && (
                  <div className="text-xs text-[#C05A3C] mt-1 inline-flex items-center gap-1">
                    <Sparkles className="w-3 h-3"/> Just £{meRow.gap_to_prize_zone.toFixed(2)} from the prize zone
                  </div>
                )}
                {meRow.me.in_prize_zone && (
                  <Badge className="mt-1 bg-[#D9923B] text-white">In the prize zone 🏆</Badge>
                )}
              </div>
            </div>
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#1A2024] text-white text-sm font-semibold hover:bg-[#2A3035]"
              data-testid="my-leaderboard-share-btn"
            >
              Grab your share link <ArrowRight className="w-4 h-4"/>
            </Link>
          </div>
        </Card>
      )}

      {/* Leaderboard */}
      <Card className="p-0 bg-white border-black/10 overflow-hidden">
        <div className="px-5 py-3 border-b border-black/5 flex items-center justify-between">
          <h2 className="font-display text-xl">Leaders</h2>
          <Badge variant="outline" className="text-xs">{data.leaders.length} sharers</Badge>
        </div>
        <div>
          {data.leaders.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground text-sm">
              No referrals yet this month — be the first.
            </div>
          ) : (
            <ul>
              {data.leaders.map((l) => {
                const isMe = user && l.user_id === user.user_id;
                const podium = l.position <= 3 ? POSITION_COLORS[l.position - 1] : null;
                return (
                  <li
                    key={l.user_id}
                    data-testid={`leaderboard-row-${l.position}`}
                    className={`flex items-center gap-4 px-5 py-4 border-b border-black/5 last:border-b-0 ${isMe ? "bg-[#FFF7EE]" : l.in_prize_zone ? "bg-[#FFFCEF]" : ""}`}
                  >
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center font-display text-sm shrink-0 text-white"
                      style={{ background: podium || "#1A2024" }}
                    >
                      {l.position <= 3 ? <Crown className="w-4 h-4"/> : `#${l.position}`}
                    </div>
                    <div className="w-9 h-9 rounded-full bg-[#F3EFE9] overflow-hidden flex items-center justify-center text-sm font-semibold shrink-0">
                      {l.picture
                        ? <img src={l.picture.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${l.picture}` : l.picture} alt="" className="w-full h-full object-cover"/>
                        : l.name.slice(0, 1)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 truncate">
                        <span className="font-semibold truncate">{l.name}</span>
                        {l.verified_badge && <BadgeCheck className="w-4 h-4 text-[#4A5D23]"/>}
                        {isMe && <Badge className="bg-[#C05A3C] text-white text-[10px]">You</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground">{l.country || ""} · {l.count} sale{l.count !== 1 ? "s" : ""}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-lg">£{l.total.toFixed(2)}</div>
                      {l.in_prize_zone && <div className="text-[10px] text-[#D9923B] uppercase tracking-wider">Prize zone</div>}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {data.leaders.length > 0 && data.leaders.length < data.prize_top_n && (
          <div className="px-5 py-3 text-xs text-muted-foreground bg-[#FAF7F2] border-t border-black/5">
            Only {data.leaders.length} of {data.prize_top_n} prize-zone spots filled — your next referral could put you in.
          </div>
        )}
      </Card>
    </div>
  );
};

export default ReferralLeaderboard;
